'use client';

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../components/icons';
import { useOptionalAdoptShelterQuizThemeVars } from '../../adopt-quiz/components/AdoptShelterQuizThemeVarsContext';
import { Button } from '../../../components/ui/Button';
import { ROUTES } from '../../../constants/routes';
import { cn } from '../../../lib/utils';
import {
  trackAdoptionApplicationClick,
  type Ga4Surface,
} from '../../../lib/analytics';
import { getMatchResultsPublicUrl } from '../lib/matchResultsPersistenceStub';
import type { RankedAnimal } from '../types';
import { MatchResultPetCard } from './MatchResultPetCard';
import { MatchShareResultsFlow } from './MatchShareResultsFlow';

export type MatchQuizResultsViewProps = {
  petType: 'Cat' | 'Dog';
  results: RankedAnimal[];
  /** From latest `POST /match` response when the server persisted shareable results. */
  shareMatchResultsId?: string | null;
  onBack: () => void;
  /** Full quiz restart — species gate with a clean profile (active quiz flow only). */
  onRestart?: () => void;
  className?: string;
  /** When there are zero matches, replaces the default empty copy (e.g. shelter-scoped quiz). */
  emptyRosterMessage?: string | null;
  /** GA4 tenant scope for match impression / profile click events. */
  shelterId?: string | null;
  /** GA4 surface for result card events (default `results`). */
  analyticsSurface?: Ga4Surface;
  /** External adoption application URL — shows CTA when set. */
  adoptionApplicationUrl?: string | null;
};

function profileHrefForAnimal(animal: RankedAnimal['animal']): string {
  const u = animal.ProfileUrl?.trim();
  if (u && /^https?:\/\//i.test(u)) return u;
  /** POC fallback when no external profile URL is set. */
  return `${ROUTES.matchingPoc}#pet-${encodeURIComponent(animal.ID)}`;
}

export function MatchQuizResultsView({
  petType,
  results,
  shareMatchResultsId = null,
  onBack,
  onRestart,
  className,
  emptyRosterMessage = null,
  shelterId = null,
  analyticsSurface = 'results',
  adoptionApplicationUrl = null,
}: MatchQuizResultsViewProps) {
  const { t } = useTranslation('matching');
  const portalShelterThemeVars = useOptionalAdoptShelterQuizThemeVars();
  const [favoriteIds, setFavoriteIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [shareFlowOpen, setShareFlowOpen] = useState(false);
  const shareTriggerRef = useRef<HTMLButtonElement>(null);

  const toggleFavorite = useCallback((id: string) => {
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const hasMatches = results.length > 0;
  const adoptionUrl = adoptionApplicationUrl?.trim() ?? '';

  const handleAdoptionApplicationClick = () => {
    if (!adoptionUrl) return;
    trackAdoptionApplicationClick({
      surface: analyticsSurface,
      shelterId,
    });
    window.open(adoptionUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={cn(
        'min-h-dvh bg-background pb-16 pt-6 text-foreground sm:pb-20 sm:pt-8',
        className,
      )}
    >
      <div className='mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8'>
        <header className='space-y-4 lg:space-y-6'>
          <div className='flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-8'>
            <div className='min-w-0 flex-1 space-y-4'>
              <div className='flex flex-col items-start gap-2'>
                <Button
                  type='button'
                  variant='outlineHighlight'
                  size='sm'
                  onClick={onBack}
                  className='gap-2'
                >
                  <Icon name='arrow-left' className='size-4' />
                  {t('results.back')}
                </Button>
                {onRestart ? (
                  <Button
                    type='button'
                    variant='outlineHighlight'
                    size='sm'
                    onClick={onRestart}
                    className='gap-2'
                  >
                    <Icon name='undo' className='size-4' />
                    {t('results.restart')}
                  </Button>
                ) : null}
              </div>
              <div>
                <h1 className='font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl'>
                  {t('results.heading')}
                </h1>
                <p className='mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base'>
                  {t('results.subtitle')}
                </p>
              </div>
            </div>
            <div className='flex w-full shrink-0 flex-col gap-2 lg:w-auto lg:self-start'>
              <Button
                ref={shareTriggerRef}
                type='button'
                variant='primary'
                disabled={!hasMatches}
                aria-disabled={!hasMatches}
                aria-describedby={
                  !hasMatches ? 'match-results-share-help' : undefined
                }
                onClick={() => setShareFlowOpen(true)}
                className='w-full'
              >
                <Icon name='upload' className='size-5' />
                {t('results.share')}
              </Button>
              {adoptionUrl ? (
                <Button
                  type='button'
                  variant='secondary'
                  disabled={!hasMatches}
                  aria-disabled={!hasMatches}
                  onClick={handleAdoptionApplicationClick}
                  className='w-full'
                >
                  {t('results.adoption')}
                  <Icon name='arrow-right' className='size-4' />
                </Button>
              ) : null}
            </div>
          </div>
          {!hasMatches ? (
            <p id='match-results-share-help' className='sr-only'>
              {t('results.shareHelp')}
            </p>
          ) : null}
        </header>

        {results.length === 0 ? (
          <div
            className='mt-12 rounded-2xl border border-border bg-card p-8 text-center shadow-sm sm:p-12'
            role='status'
          >
            <p className='font-heading text-lg font-semibold text-card-foreground'>
              {t('results.noMatches')}
            </p>
            <p className='mt-2 text-sm text-muted-foreground'>
              {emptyRosterMessage ??
                t('results.empty', {
                  species: t(petType === 'Cat' ? 'animal.cat' : 'animal.dog'),
                })}
            </p>
          </div>
        ) : (
          <ul className='mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4'>
            {results.map(({ animal, matchPercent }, index) => (
              <li
                key={animal.ID}
                className='flex h-full min-h-0 min-w-0 items-stretch'
              >
                <MatchResultPetCard
                  animal={animal}
                  matchPercent={matchPercent}
                  profileHref={profileHrefForAnimal(animal)}
                  isFavorite={favoriteIds.has(animal.ID)}
                  onToggleFavorite={() => toggleFavorite(animal.ID)}
                  className='min-h-0 w-full min-w-0 flex-1'
                  analytics={{
                    species: petType,
                    rank: index + 1,
                    surface: analyticsSurface,
                    shelterId,
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {hasMatches ? (
        <MatchShareResultsFlow
          isOpen={shareFlowOpen}
          onClose={() => setShareFlowOpen(false)}
          restoreFocusRef={shareTriggerRef}
          results={results}
          publicShareUrl={getMatchResultsPublicUrl(shareMatchResultsId)}
          shareMatchResultsId={shareMatchResultsId}
          hideManualSlideControls={true}
          portalShelterThemeVars={portalShelterThemeVars}
        />
      ) : null}
    </div>
  );
}
